import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData } from "@tanstack/react-query";
import { Box, LayoutGrid, Store, Plus, PackageOpen, ChevronLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApiQuery } from "../lib/useApiQuery";
import { useT } from "../i18n/LanguageContext";
import { ProductCard } from "../components/ProductCard";
import { Loader, EmptyState } from "../components/Loader";
import { Verified } from "../components/Verified";
import DriverHome from "./driver/DriverHome";

export default function Home() {
  const { user, guest } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const isSeller = user?.role === "seller";
  const [activeInterest, setActiveInterest] = useState(null);
  const [sellerView, setSellerView] = useState("mine");

  useEffect(() => {
    if (!user || guest) return;
    if (user.is_admin) return;
    if ((user.role === "buyer" || user.role === "driver") && !user.onboarded) {
      navigate("/interests-onboarding");
    }
  }, [user, guest]); // eslint-disable-line

  const { data: interests = [] } = useApiQuery(["interests"], "/interests");

  const productsUrl = (isSeller && user && sellerView === "mine")
    ? `/products?seller_id=${user.user_id}`
    : activeInterest ? `/products/feed?interest_id=${activeInterest}` : "/products/feed";
  const { data: rawProducts = [], isLoading } = useApiQuery(
    ["products", { view: isSeller ? sellerView : "buyer", uid: user?.user_id || null, interest: activeInterest }],
    productsUrl,
    { placeholderData: keepPreviousData }
  );
  const products = (isSeller && user && sellerView === "market")
    ? rawProducts.filter((p) => p.seller_id !== user.user_id)
    : rawProducts;

  const { data: minePromos = [] } = useApiQuery(
    ["promotions", "mine"],
    "/promotions/mine",
    { enabled: !!(isSeller && user && sellerView === "mine") }
  );
  const activePromoIds = new Set(
    minePromos
      .filter((p) => p.status === "active" && new Date(p.expires_at) > new Date() && p.current_impressions < p.target_impressions)
      .map((p) => p.product_id)
  );

  if (user?.role === "driver") return <DriverHome />;

  const showingOwn = isSeller && sellerView === "mine";
  const greeting = isSeller
    ? `${lang === "ar" ? "أهلاً،" : "Hi,"} ${user?.store_name || user?.name}`
    : guest ? (lang === "ar" ? "مرحبًا بك" : "Welcome") : `${lang === "ar" ? "أهلاً" : "Hi"} ${user?.name || ""}`;

  const rootInterests = interests.filter((it) => !it.parent_id);

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-sm text-[#6B7280] font-semibold">{greeting} 👋</p>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-black text-[#161629]">
              {showingOwn ? (lang === "ar" ? "منتجاتك" : "Your Products") : t("app_name")}
            </h1>
            {user?.verified && <Verified size={20} />}
          </div>
        </div>
        {isSeller && (
          <button data-testid="header-add-product" onClick={() => navigate("/add-product")} className="w-11 h-11 rounded-2xl bg-[#FF3D5A] text-white flex items-center justify-center brand-shadow-sm hover:bg-[#FF2548]">
            <Plus size={22} />
          </button>
        )}
      </div>

      {isSeller && (
        <div className="flex gap-1 p-1 bg-[#F7F8FE] rounded-2xl mb-4 max-w-md">
          <button data-testid="seller-view-mine" onClick={() => setSellerView("mine")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-extrabold text-sm ${sellerView === "mine" ? "bg-[#FF3D5A] text-white brand-shadow-sm" : "text-[#161629]"}`}>
            <Box size={16} /> {lang === "ar" ? "منتجاتي" : "My Products"}
          </button>
          <button data-testid="seller-view-market" onClick={() => setSellerView("market")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-extrabold text-sm ${sellerView === "market" ? "bg-[#FF3D5A] text-white brand-shadow-sm" : "text-[#161629]"}`}>
            <Store size={16} /> {lang === "ar" ? "تصفّح المتاجر" : "Browse Marketplace"}
          </button>
        </div>
      )}

      {(!isSeller || sellerView === "market") && rootInterests.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-black text-[#161629]">{lang === "ar" ? "تصفّح الاهتمامات" : "Browse Interests"}</h2>
            <button data-testid="browse-see-all" onClick={() => navigate("/browse/root?type=interest")} className="flex items-center gap-1 text-[#FF3D5A] font-extrabold text-xs">
              {lang === "ar" ? "الكل" : "See All"} <ChevronLeft size={14} />
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
            <button data-testid="chip-all" onClick={() => setActiveInterest(null)} className={`flex items-center gap-2 px-4 py-2 rounded-full border font-extrabold text-sm whitespace-nowrap ${!activeInterest ? "bg-[#FF3D5A] border-[#FF3D5A] text-white" : "bg-white border-[#E8EAF2] text-[#161629]"}`}>
              <LayoutGrid size={16} /> {t("all_lists")}
            </button>
            {rootInterests.map((it) => {
              const isActive = activeInterest === it.interest_id;
              const hasChildren = interests.some((x) => x.parent_id === it.interest_id);
              return (
                <button
                  key={it.interest_id}
                  data-testid={`chip-${it.interest_id}`}
                  onClick={() => hasChildren ? navigate(`/browse/${it.interest_id}?type=interest`) : setActiveInterest(isActive ? null : it.interest_id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border font-extrabold text-sm whitespace-nowrap ${isActive ? "text-white" : "bg-white border-[#E8EAF2] text-[#161629]"}`}
                  style={isActive ? { backgroundColor: it.color || "#FF3D5A", borderColor: it.color || "#FF3D5A" } : {}}
                >
                  <span style={{ color: isActive ? "#fff" : it.color }}>●</span>
                  {it.name}{hasChildren ? " ›" : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isLoading ? (
        <Loader />
      ) : products.length === 0 ? (
        <EmptyState
          icon={PackageOpen}
          title={t("no_products")}
          action={showingOwn ? (
            <button data-testid="empty-add-product" onClick={() => navigate("/add-product")} className="bg-[#FF3D5A] text-white font-extrabold px-6 py-3 rounded-2xl">+ {t("add_product")}</button>
          ) : null}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {products.map((item) => {
            const promoted = !!item.is_promoted || (showingOwn && activePromoIds.has(item.product_id));
            return <ProductCard key={item.product_id} item={item} promoted={promoted} />;
          })}
        </div>
      )}
    </div>
  );
}
